import {useCallback} from 'react';

import {LastSyncedUpstreamValue} from 'notebook/structured-tables/StructuredTableConstants';
import {GetActionFunctionsParams} from 'notebook/structured-tables/util/getActionFunctions';
import {Table, TableData} from 'notebook/tables/constants/TableConstants';
import {TableDimension} from 'notebook/tables/constants/TableSelectionConstants';
import {TableOpDiffMap, TableOpFunction} from 'notebook/tables/excess/ops/TableOpConstants';
import {unmergeSingleInput} from 'notebook/tables/excess/ops/TableOps';
import {TokenUpdater} from 'notebook/tables/excess/ops/TokenUpdater';
import toRanges from 'notebook/tables/excess/util/toRanges';

/**
 * Returns a callback that unmerges the given single inputs in a table and commits the result.
 *
 * The unmerge, the cross-table/cross-document formula shifting it triggers (single inputs change
 * column address when unmerged, so references to them must move), and the provided structured-table
 * content (`lastSyncedUpstreamValuesByRow`) are all applied within a single `performTableOp` root
 * update. Committing in one shot is essential: a second, separate update would rebuild the note from
 * a stale root snapshot and silently revert the formula shifts on every table other than the target.
 */
export default function useUnmergeBrokenSingleInputsCallback(
  performTableOp: GetActionFunctionsParams<unknown, unknown>['performTableOp']
): (
  refreshedTable: Table,
  brokenSingleInputIndexes: Array<number>,
  lastSyncedUpstreamValuesByRow?: Array<Array<LastSyncedUpstreamValue>>,
  tableOpDiffMap?: TableOpDiffMap
) => void {
  return useCallback(
    (
      refreshedTable: Table,
      brokenSingleInputIndexes: Array<number>,
      lastSyncedUpstreamValuesByRow?: Array<Array<LastSyncedUpstreamValue>>,
      tableOpDiffMap?: TableOpDiffMap
    ) => {
      const tableOpFunc: TableOpFunction = (_storedTable, documentName, evaluatedTable) => {
        const continuousRanges = toRanges(brokenSingleInputIndexes);
        let outputTable = refreshedTable;
        const tokenUpdaters: Array<TokenUpdater> = [];
        for (const [start, end] of continuousRanges.reverse()) {
          const opOutput = unmergeSingleInput(outputTable, documentName, evaluatedTable, start, end);
          outputTable = opOutput.table;
          tokenUpdaters.push(...opOutput.tokenUpdaters);
        }
        // Forward the incoming per-table ops, but force the edited table to re-evaluate: unmerging
        // single inputs changes column addresses, so the table must be resynced rather than applying
        // the caller's original diff for it.
        const tableOp: TableOpDiffMap = {...tableOpDiffMap, [refreshedTable.name]: {type: 'eval-table'}};
        return {table: outputTable, tokenUpdaters, box: null, dim: TableDimension.COLUMN, tableOp};
      };
      performTableOp({
        op: tableOpFunc,
        options: {remakeSingleInputs: false},
        ...(lastSyncedUpstreamValuesByRow != null
          ? {targetItemContent: {lastSyncedUpstreamValuesByRow}}
          : {}),
      });
    },
    [performTableOp]
  );
}

/**
 * Returns the indexes of columns that are single inputs but have broken merge cells.
 * @param table
 * @returns A list of column indexes (in order)
 */
export function getBrokenSingleInputIndexes(table: TableData): Array<number> {
  const brokenSingleInputIndexes: Array<number> = [];
  for (const [columnIndex, column] of table.columnConstraints.entries()) {
    const {mergeRowSpan} = table.table[0][columnIndex];
    if (
      column?.structuredTableColumn?.isSingleInput &&
      (mergeRowSpan == null || mergeRowSpan !== table.table.length)
    ) {
      brokenSingleInputIndexes.push(columnIndex);
    }
  }
  return brokenSingleInputIndexes;
}
